import React, { useState, useEffect } from "react";
import RequestDetails from "./request";
import { TbAlertTriangleFilled, TbBed, TbBus, TbCar, TbMap, TbPlane, TbReceipt, TbTrain } from "react-icons/tb";
import ApprovalTypes from "./Types";
import { Button, CustomInputs, Modal, ReplyInChat } from "../..";
import { Dialog, Input, Loader, Toast } from "../..";
import { apis, get, patch, post } from "../../../Utils/axios";
import OpenBooking from "./booking";
import { useNavigate } from "react-router-dom";
import { FiArrowRight, FiX } from "react-icons/fi";
import { getFlightApprovalSearchUrlFromRequestData, isFlightApprovalReadyToBook } from "../../../Utils/flightApproval";

function ViewApproval({ approvalId, onReload = () => {}, onClose = () => {}, readOnly }) {
	const NAVIGATE = useNavigate();
	const ICONS = {
		trip: <TbMap />,
		expense: <TbReceipt />,
		flight: <TbPlane />,
		bus: <TbBus />,
		stay: <TbBed />,
		train: <TbTrain />,
		cab: <TbCar />,
	};
	const [ConfirmDialog, setConfirmDialog] = useState();
	const [Approval, setApproval] = useState({
		request: {},
		approvers: [],
		type: "",
		status: "",
		amount: "",
		date: "",
		id: "",
		trail: "",
		violations: [],
		customInput: {},
		requestedBy: {},
	});

	const [Remarks, setRemarks] = useState("");
	const [Loading, setLoading] = useState(true);

	useEffect(() => {
		if (approvalId) {
			fetchApproval(approvalId);
		}
	}, [approvalId]);

	const fetchApproval = (approvalId) => {
		setLoading(true);
		get(`/approval/${approvalId}`, {}, (r, e) => {
			if (r) {
				setApproval(r);
				setLoading(false);
			} else if (e) {
				Toast.handleError(e);
				setLoading(false);
			}
		});
	};

	const actionOnApproval = (action) => {
		if (action === "reject" && !Remarks) {
			return Toast.error("Please enter remarks");
		}
		patch(`/approval/${approvalId}/${action}`, { action, remarks: Remarks }, (r, e) => {
			if (r) {
				setConfirmDialog();
				Toast.success(`Approval request ${action}`);
				fetchApproval(approvalId);
				setLoading(false);
				onReload();
			} else if (e) {
				Toast.handleError(e);
				setLoading(false);
			}
		});
	};

	const withdrawApproval = () => {
		post(apis.withdrawApproval, { approvalId }, (r, e) => {
			if (r) {
				setConfirmDialog();
				Toast.success(`Approval request withdrawn`);
				fetchApproval(approvalId);
				setLoading(false);
				onReload();
			} else if (e) {
				Toast.handleError(e);
				setLoading(false);
			}
		});
	};

	// const replyInChat = () => {
	// 	return {
	// 		content: Approval,
	// 		type: "approval",
	// 		userId: Approval?.requestedBy?.id,
	// 	};
	// };

	const approvalFlight = Approval?.request?.data?.flight || {};
	const bookNowUrl =
		Approval?.type === "flight"
			? getFlightApprovalSearchUrlFromRequestData({
					approvalId,
					requestData: Approval?.request?.data,
				})
			: null;
	const showBookNow =
		Approval?.type === "flight" &&
		!!bookNowUrl &&
		isFlightApprovalReadyToBook({
			approvalRequest: true,
			status: Approval?.status,
			departureTime: approvalFlight?.onwardSegments?.[0]?.departureTime || approvalFlight?.segments?.[0]?.departureTime,
		});
	const hasViolations = Approval?.violations?.length > 0;
	const showDecisionActions = showBookNow || (Approval.status === "pending" && !readOnly);

	return (
		<>
			<Modal contentClass="!max-w-5xl !bg-light !w-full" open={approvalId} onClose={onClose} showCloseButton={false}>
				<div className="flex flex-col p-6 gap-y-4">
					<div className="flex items-start justify-between gap-3">
						<h1 className="capitalize align-center gap-x-2">
							{ICONS[Approval?.type]}
							{Approval?.type} Approval
						</h1>
						<button
							type="button"
							onClick={onClose}
							className="shrink-0 p-1.5 rounded-md text-secondary hover:text-dark hover:bg-canvas focus:outline-none focus:ring-2 focus:ring-primary"
							aria-label="Close"
						>
							<FiX className="w-5 h-5" aria-hidden="true" />
						</button>
					</div>
					<div className="flex flex-col gap-4 my-4">
						<div className="flex flex-col gap-4">
							<OpenBooking {...Approval} />
							<ApprovalTypes type={Approval?.type} request={Approval?.request?.data} receipt={Approval?.receipt} />
							<CustomInputs className="p-4 rounded-md shadow-sm bg-canvas" fields={Approval.customInput?.fields} />
						</div>
						<div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
							<div className="lg:col-span-3">
								<RequestDetails {...Approval} showRequestDetails={false} showApprovers={false} showViolations={false} />
							</div>
							<div className="lg:col-span-2">
								<RequestDetails {...Approval} showRequestDetails={true} showRequestedBy={false} showApprovers={false} showViolations={false} />
							</div>
						</div>
						{Approval?.request?.notes && (
							<div className="p-4 rounded-lg shadow-sm bg-canvas">
								<label>Notes from user</label>
								<div className="text-label">{Approval?.request?.notes}</div>
							</div>
						)}
						{(hasViolations || showDecisionActions) && (
							<div className="rounded-xl border border-surface bg-canvas p-4 shadow-sm lg:p-5">
								<div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
									<div className="flex-1 min-w-0">
										<h4>Decision Summary</h4>
										<div className="mt-1 text-sm text-secondary">
											{Approval.status === "pending" && !readOnly
												? "Review the policy guidance before you approve or decline this request."
												: showBookNow
													? "This request is approved and ready to continue to booking."
													: "Review the policy guidance attached to this request."}
										</div>
										{hasViolations ? (
											<div className="mt-3 rounded-lg border border-danger bg-danger px-3 py-2 text-dangerText">
												<div className="flex items-start gap-2">
													<TbAlertTriangleFilled className="mt-0.5 h-4 w-4 shrink-0" />
													<div className="min-w-0">
														<div className="text-sm font-semibold">
															{Approval?.violations?.length} Policy Violation{Approval?.violations?.length > 1 ? "s" : ""}
														</div>
														<div className="mt-1 text-xs leading-5">
															{Approval?.violations?.map((violation, index) => (
																<div key={`${violation}-${index}`}>{index + 1}. {violation}</div>
															))}
														</div>
													</div>
												</div>
											</div>
										) : null}
									</div>
									{showDecisionActions ? (
										<div className="flex w-full shrink-0 flex-col gap-2 sm:flex-row lg:w-auto lg:flex-wrap lg:justify-end">
											{showBookNow && (
												<Button
													onClick={() => NAVIGATE(bookNowUrl)}
													variant="primary"
													className="w-full gap-2 sm:w-auto lg:min-w-[10rem]"
												>
													Book Now <FiArrowRight />
												</Button>
											)}
											{Approval.status === "pending" && !readOnly ? (
												<>
													<Button loading={Loading} onClick={() => setConfirmDialog("approve")} variant="success" className="w-full sm:w-auto lg:min-w-[10rem]">
														Approve
													</Button>
													<Button loading={Loading} onClick={() => setConfirmDialog("decline")} variant="danger" className="w-full sm:w-auto lg:min-w-[10rem]">
														Decline
													</Button>
													{/* <ReplyInChat onClick={replyInChat} /> */}
												</>
											) : null}
										</div>
									) : null}
								</div>
							</div>
						)}
						<RequestDetails {...Approval} showRequestDetails={false} showRequestedBy={false} showViolations={false} />
					</div>
				</div>
				<Loader loading={Loading} />
				<Dialog variant="success" open={ConfirmDialog === "approve"} title="Approve Request" description="Are you sure you want to approve this request" submitLabel="Approve" onSubmit={() => actionOnApproval("approve")} onClose={setConfirmDialog} />
				<Dialog variant="danger" open={ConfirmDialog === "withdraw"} title="Withdraw Request" description="Are you sure you want to withdraw this request" submitLabel="Withdraw" onSubmit={withdrawApproval} onClose={setConfirmDialog} />
				<Dialog variant="danger" open={ConfirmDialog === "decline"} title="Decline Request" description="Are you sure you want to decline this request" submitLabel="Decline" onSubmit={() => actionOnApproval("reject")} onClose={setConfirmDialog}>
					<div className="px-6">
						<Input onChange={(value) => setRemarks(value)} value={Remarks} placeholder="Reason for decline" required />
					</div>
				</Dialog>
			</Modal>
		</>
	);
}

export default ViewApproval;
